import type { TokenPair, AuthError } from "../types";
import { sessionCore } from "./session-core";
import { authApi, AuthApiError } from "../api";
import {
  TOKEN_REFRESH_THRESHOLD_MS,
  REFRESH_MAX_RETRIES,
  REFRESH_RETRY_DELAYS,
} from "../constants";
import { parseApiTokens } from "./jwt-decode";

let inflightRefresh: Promise<TokenPair | null> | null = null;

/** 单飞去重刷新 — 多个并发调用共享同一个 promise */
export async function refreshWithDedup(): Promise<TokenPair | null> {
  if (inflightRefresh) {
    return inflightRefresh;
  }
  inflightRefresh = executeRefresh().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

/** 请求前拦截：检查是否需要刷新 */
export async function ensureFreshToken(): Promise<string | null> {
  const tokens = sessionCore.getTokens();
  if (!tokens?.accessToken) {
    return null;
  }

  if (!sessionCore.isExpiringSoon(TOKEN_REFRESH_THRESHOLD_MS)) {
    return tokens.accessToken;
  }

  if (!sessionCore.canRefresh()) {
    return tokens.accessToken;
  }

  const refreshed = await refreshWithDedup();
  return refreshed?.accessToken ?? tokens.accessToken;
}

async function executeRefresh(): Promise<TokenPair | null> {
  const tokens = sessionCore.getTokens();
  if (!tokens?.refreshToken) {
    return null;
  }

  for (let attempt = 0; attempt <= REFRESH_MAX_RETRIES; attempt++) {
    try {
      const result = await authApi.refresh(tokens.refreshToken);
      const newTokens = parseApiTokens(result);
      sessionCore.setTokens(newTokens);
      return newTokens;
    } catch (error) {
      const classified = classifyError(error);

      if (classified.type === "auth_expired") {
        return null;
      }

      if (classified.type === "network" || classified.type === "server_error") {
        if (attempt < REFRESH_MAX_RETRIES) {
          await delay(REFRESH_RETRY_DELAYS[attempt] ?? 3000);
          continue;
        }
        return tokens;
      }
    }
  }
  return null;
}

/** 错误分类 — 核心原则：只有认证失败才登出 */
export function classifyError(error: unknown): AuthError {
  if (error instanceof AuthApiError) {
    if (error.isAuthFailure) {
      return { type: "auth_expired", message: error.body };
    }
    if (error.isServerError) {
      return {
        type: "server_error",
        status: error.status,
        message: error.body,
      };
    }
    return { type: "network", message: error.message };
  }
  if (error instanceof TypeError) {
    return { type: "network", message: error.message };
  }
  return { type: "network", message: String(error) };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 暴露用于测试的重置方法 */
export function _resetInflight(): void {
  inflightRefresh = null;
}
